import React from 'react';
import { Col, Form, InputGroup, Row } from '@themesberg/react-bootstrap';
import DatePickerField from './DatePickerField';
import PoPaymentTermSelect from './PoPaymentTermSelect';
import QuoteProductsTable from './QuoteProductsTable';
import { poSalesStatusOptions, poToPrincipalVendorOptions } from '../utils/poSales';
import { quoteProductSummary, quoteProductsSchema } from '../utils/quoteProducts';
import * as Yup from 'yup';

export const poSalesFormSchema = (terms, savedPaymentTerm = '', requirePoNumber = false) => Yup.object({
  products: quoteProductsSchema,
  // Typed once when converting; existing POs show it read-only and are never re-validated.
  po_number: requirePoNumber ? Yup.string().trim().max(100).required('PO Number is required') : Yup.string(),
  po_release_date: Yup.string().required('PO release date is required'),
  po_received_date: Yup.string().required('PO received date is required'),
  delivery_due_date: Yup.string().required('Delivery due date is required'),
  po_to_principal_vendor: Yup.string().oneOf(poToPrincipalVendorOptions).required('Please select Yes or No'),
  payment_term: Yup.string().max(100).required('Payment term is required')
    .oneOf([...terms.map(term => term.name), savedPaymentTerm].filter(Boolean), 'Select an available payment term.'),
  status: Yup.string().oneOf(poSalesStatusOptions).required('Status is required'),
  remarks: Yup.string(),
});

export const poSalesPayload = values => ({
  products: values.products.map(({ id, _key, ...row }) => ({ ...(id != null ? { id } : {}), ...row })),
  po_release_date: values.po_release_date,
  po_received_date: values.po_received_date,
  delivery_due_date: values.delivery_due_date,
  po_to_principal_vendor: values.po_to_principal_vendor,
  payment_term: values.payment_term,
  status: values.status,
  remarks: values.remarks,
});

const formatDetailDate = value => {
  const stored = String(value || '').slice(0, 10);
  const [year, month, day] = stored.split('-');
  return year && month && day ? `${month}/${day}/${year}` : (value || '');
};

const QuoteValue = ({ label, value }) => (
  <Col md={3} className="mb-3">
    <div className="po-sales-quote-detail">
      <div className="po-sales-quote-detail__label">{label}</div>
      <div className="po-sales-quote-detail__value">{value || value === 0 ? value : '—'}</div>
    </div>
  </Col>
);

export default function PoSalesFormFields({ formik, quote, terms, actions, poNumberReadOnly = false }) {
  const fields = [
    ['po_number', 'PO Number', 'po-number'],
    ['po_release_date', 'PO Release Date', 'date'],
    ['po_received_date', 'PO Received Date', 'date'],
    ['po_amount', 'PO Amount', 'money'],
    ['delivery_due_date', 'Delivery Due Date', 'date'],
    ['po_to_principal_vendor', 'PO to Principal/Vendor', 'select', poToPrincipalVendorOptions],
    ['payment_term', 'Payment Term', 'payment-term'],
    ['status', 'Status', 'select', poSalesStatusOptions],
  ];

  return (
    <>
      {formik.status && <div role="alert" className="alert alert-danger">{formik.status}</div>}
      <Row className="mb-3">
        <Col md={12}><h5 className="mb-3">Quote Details</h5></Col>
        <QuoteValue label="System Quote No." value={quote?.system_quote_no} />
        <QuoteValue label="Offer No." value={quote?.offer_no} />
        <QuoteValue label="Customer" value={quote?.customer?.name} />
        <QuoteValue label="Company" value={quote?.company?.name} />
        <QuoteValue label="Branch" value={quote?.branch?.name} />
        <QuoteValue label="Principal / Vendor" value={quote?.principal?.name} />
        <QuoteValue label="Category" value={quote?.category?.name} />
        <QuoteValue label="Offer Date" value={formatDetailDate(quote?.offer_date)} />
        <QuoteValue label="Expected Date" value={formatDetailDate(quote?.expected_date)} />
        <Col md={12} className="mb-3">
          <Form.Group controlId="po-sales-remarks">
            <Form.Label>Remarks</Form.Label>
            <Form.Control as="textarea" rows={3} {...formik.getFieldProps('remarks')}
              disabled={formik.isSubmitting} isInvalid={Boolean(formik.touched.remarks && formik.errors.remarks)} />
            <Form.Control.Feedback type="invalid">{formik.errors.remarks}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <QuoteProductsTable formik={formik} />
      <hr />

      <Row className="mb-3">
        <Col md={12}><h5 className="mb-3">PO Sales Details</h5></Col>
        {fields.map(([name, label, type, options]) => (
          <Col md={3} className="mb-3" key={name}>
            {type === 'payment-term' ? (
              <PoPaymentTermSelect id="po-sales-payment-term" formik={formik}
                terms={terms} savedValue={formik.values.payment_term || ''} />
            ) : (
              <Form.Group controlId={'po-sales-' + name}>
                <Form.Label htmlFor={'po-sales-' + name}>{label}{name !== 'status' && name !== 'po_amount' && !(name === 'po_number' && poNumberReadOnly) && <span className="text-danger">*</span>}</Form.Label>
                {type === 'select' ? (
                  <Form.Select id={'po-sales-' + name} {...formik.getFieldProps(name)} disabled={formik.isSubmitting}
                    isInvalid={Boolean(formik.touched[name] && formik.errors[name])}>
                    <option value="">Select {label}</option>
                    {options.map(option => <option key={option} value={option}>{option}</option>)}
                  </Form.Select>
                ) : type === 'po-number' ? (
                  <Form.Control id="po-sales-po_number" {...formik.getFieldProps('po_number')} maxLength={100}
                    readOnly={poNumberReadOnly} disabled={formik.isSubmitting && !poNumberReadOnly}
                    isInvalid={Boolean(formik.touched.po_number && formik.errors.po_number)} />
                ) : type === 'money' ? (
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    {/* Always the Quote total of the product table; never typed in by the user. */}
                    <Form.Control id={'po-sales-' + name} name={name} readOnly
                      value={quoteProductSummary(formik.values.products).total} />
                  </InputGroup>
                ) : type === 'date' ? (
                  <DatePickerField
                    id={'po-sales-' + name}
                    name={name}
                    value={formik.values[name]}
                    onChange={value => formik.setFieldValue(name, value)}
                    onBlur={() => formik.setFieldTouched(name, true)}
                    disabled={formik.isSubmitting}
                    isInvalid={Boolean(formik.touched[name] && formik.errors[name])}
                  />
                ) : (
                  <Form.Control id={'po-sales-' + name} {...formik.getFieldProps(name)} disabled={formik.isSubmitting}
                    isInvalid={Boolean(formik.touched[name] && formik.errors[name])} />
                )}
                <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
              </Form.Group>
            )}
          </Col>
        ))}
      </Row>
      {actions}
    </>
  );
}
